// frontend/src/components/admin/FeaturedManagement.jsx
import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import featuredService from "../../services/featuredService";
import { Plus, Edit2, Trash2, Eye, EyeOff, Loader2, X, Upload, Link as LinkIcon, Sparkles, Image as ImageIcon } from "lucide-react";

export default function FeaturedManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // 🔥 Previews para ambas imágenes
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [lightboxPreview, setLightboxPreview] = useState(null);
  const [lightboxFile, setLightboxFile] = useState(null);

  const [formData, setFormData] = useState({
    titulo: "",
    descricao: "",
    link: "",
    tipo: "anuncio",
    post_id: null,
    ativo: true,
    ordem: 0,
    cor: "from-emerald-500 to-green-600",
    show_title: true,
    show_description: true,
  });

  const coresDisponiveis = [
    { label: "Verde Esmeralda", value: "from-emerald-500 to-green-600" },
    { label: "Azul", value: "from-blue-500 to-indigo-600" },
    { label: "Âmbar", value: "from-amber-500 to-yellow-600" },
    { label: "Violeta", value: "from-violet-500 to-purple-600" },
    { label: "Rosa", value: "from-rose-500 to-pink-600" },
    { label: "Vermelho", value: "from-red-500 to-orange-600" },
    { label: "Roxo", value: "from-purple-500 to-indigo-600" },
  ];

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    setLoading(true);
    const result = await featuredService.getAll();
    if (result.success) {
      setItems(result.data);
    }
    setLoading(false);
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({
      titulo: "",
      descricao: "",
      link: "",
      tipo: "anuncio",
      post_id: null,
      ativo: true,
      ordem: 0,
      cor: "from-emerald-500 to-green-600",
      show_title: true,
      show_description: true,
    });
    setImagePreview(null);
    setImageFile(null);
    setLightboxPreview(null);
    setLightboxFile(null);
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      titulo: item.titulo,
      descricao: item.descricao || "",
      link: item.link || "",
      tipo: item.tipo,
      post_id: item.post_id || null,
      ativo: item.ativo,
      ordem: item.ordem || 0,
      cor: item.cor || "from-emerald-500 to-green-600",
      show_title: item.show_title !== undefined ? item.show_title : true,
      show_description: item.show_description !== undefined ? item.show_description : true,
    });
    setImagePreview(item.imagem_url || null);
    setImageFile(null);
    setLightboxPreview(item.imagem_lightbox_url || null);
    setLightboxFile(null);
    setIsModalOpen(true);
  };

  // 🔥 Manejar cambio de imagen principal (carousel)
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // 🔥 NUEVO: Manejar cambio de imagen del lightbox
  const handleLightboxChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLightboxFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLightboxPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // 🔥 NUEVO: Eliminar imagen del lightbox
  const removeLightboxImage = () => {
    setLightboxPreview(null);
    setLightboxFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!formData.titulo.trim()) {
        Swal.fire("Erro!", "O título é obrigatório.", "error");
        setSubmitting(false);
        return;
      }

      if (!formData.tipo) {
        Swal.fire("Erro!", "O tipo é obrigatório.", "error");
        setSubmitting(false);
        return;
      }

      const formPayload = new FormData();
      formPayload.append('titulo', formData.titulo);
      formPayload.append('tipo', formData.tipo);
      formPayload.append('descricao', formData.descricao || '');
      formPayload.append('link', formData.link || '');
      formPayload.append('ordem', formData.ordem || 0);
      formPayload.append('ativo', formData.ativo ? '1' : '0');
      formPayload.append('cor', formData.cor || 'from-emerald-500 to-green-600');
      formPayload.append('show_title', formData.show_title ? '1' : '0');
      formPayload.append('show_description', formData.show_description ? '1' : '0');

      // 🔥 Imagen principal (carousel)
      if (imageFile) {
        formPayload.append('imagem', imageFile);
      }

      // 🔥 NUEVO: Imagen del lightbox
      if (lightboxFile) {
        formPayload.append('imagem_lightbox', lightboxFile);
      }

      console.log('📦 Enviando:', {
        titulo: formData.titulo,
        tipo: formData.tipo,
        hasImage: !!imageFile,
        hasLightbox: !!lightboxFile,
        show_title: formData.show_title,
        show_description: formData.show_description,
      });

      let result;
      if (editingItem) {
        result = await featuredService.update(editingItem.id, formPayload);
      } else {
        result = await featuredService.create(formPayload);
      }

      if (result.success) {
        Swal.fire({
          icon: "success",
          title: editingItem ? "Destaque atualizado!" : "Destaque criado!",
          timer: 2000,
          showConfirmButton: false,
        });
        setIsModalOpen(false);
        loadItems();
      } else {
        if (result.errors) {
          const errorMessages = Object.values(result.errors).flat().join('\n');
          throw new Error(errorMessages);
        }
        throw new Error(result.message || 'Erro ao salvar');
      }
    } catch (error) {
      console.error('❌ Error:', error);
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: error.message || "Não foi possível salvar.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      const result = await featuredService.toggleActive(item.id);
      if (result.success) {
        loadItems();
      }
    } catch (error) {
      Swal.fire("Erro!", "Não foi possível alterar o status.", "error");
    }
  };

  const deleteItem = async (item) => {
    const result = await Swal.fire({
      title: "Excluir destaque?",
      text: `"${item.titulo}" será removido permanentemente.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const response = await featuredService.delete(item.id);
        if (response.success) {
          Swal.fire({
            icon: "success",
            title: "Destaque excluído!",
            timer: 2000,
            showConfirmButton: false,
          });
          loadItems();
        }
      } catch (error) {
        Swal.fire("Erro!", "Não foi possível excluir.", "error");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#449879] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#449879]" />
            Destaques
          </h3>
          <p className="text-sm text-gray-500">{items.length} item(s) cadastrado(s)</p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-[#449879] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[#367d64] transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Novo Destaque
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-2xl">
          <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nenhum destaque cadastrado</p>
          <p className="text-sm text-gray-400 mt-1">Clique em "Novo Destaque" para adicionar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div key={item.id} className={`bg-white rounded-2xl border overflow-hidden transition-all ${item.ativo ? "border-gray-200" : "border-gray-200 opacity-60"}`}>
              <div className="relative h-40 bg-gray-100 overflow-hidden">
                {item.imagem_url ? (
                  <img src={item.imagem_url} alt={item.titulo} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-gray-200 to-gray-300">
                    <span className="text-gray-500 text-sm">Sem imagem</span>
                  </div>
                )}
                {!item.ativo && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-sm font-medium bg-gray-800/80 px-3 py-1 rounded-full">Inativo</span>
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <span className="bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded-full">{item.tipo}</span>
                </div>
                <div className="absolute bottom-2 left-2">
                  <span className={`bg-gradient-to-r ${item.cor || 'from-emerald-500 to-green-600'} text-white text-[8px] px-2 py-0.5 rounded-full`}>
                    Cor
                  </span>
                </div>
                {/* 🔥 NUEVO: Badge indicando si tiene imagen lightbox */}
                {item.imagem_lightbox_url && (
                  <div className="absolute bottom-2 right-2">
                    <span className="bg-black/60 backdrop-blur-sm text-white text-[8px] px-2 py-0.5 rounded-full flex items-center gap-1">
                      <ImageIcon className="w-3 h-3" />
                      HD
                    </span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <h4 className="font-semibold text-gray-800 truncate">{item.titulo}</h4>
                {item.descricao && <p className="text-sm text-gray-500 truncate mt-1">{item.descricao}</p>}
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                  <span>Ordem: {item.ordem}</span>
                  {item.link && <span className="flex items-center gap-1"><LinkIcon className="w-3 h-3" />Link</span>}
                </div>
              </div>
              <div className="border-t border-gray-100 p-3 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-1">
                  <button onClick={() => openEditModal(item)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteItem(item)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <button onClick={() => toggleActive(item)} className={`p-1.5 rounded-lg transition-colors ${item.ativo ? "text-green-600 hover:bg-green-50" : "text-gray-400 hover:bg-gray-100"}`}>
                  {item.ativo ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#449879]" />
                {editingItem ? "Editar Destaque" : "Novo Destaque"}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                <input 
                  type="text" 
                  value={formData.titulo} 
                  onChange={(e) => setFormData({...formData, titulo: e.target.value})} 
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879]" 
                  required 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea 
                  value={formData.descricao} 
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})} 
                  rows="2" 
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879]" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link</label>
                <input 
                  type="url" 
                  value={formData.link} 
                  onChange={(e) => setFormData({...formData, link: e.target.value})} 
                  placeholder="https://..." 
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879]" 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <select 
                    value={formData.tipo} 
                    onChange={(e) => setFormData({...formData, tipo: e.target.value})} 
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879]"
                  >
                    <option value="anuncio">Anúncio</option>
                    <option value="produto">Produto</option>
                    <option value="externo">Externo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordem</label>
                  <input 
                    type="number" 
                    value={formData.ordem} 
                    onChange={(e) => setFormData({...formData, ordem: parseInt(e.target.value) || 0})} 
                    min="0" 
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879]" 
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cor do Gradiente</label>
                <select 
                  value={formData.cor} 
                  onChange={(e) => setFormData({...formData, cor: e.target.value})} 
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879]"
                >
                  {coresDisponiveis.map((cor) => (
                    <option key={cor.value} value={cor.value}>{cor.label}</option>
                  ))}
                </select>
                <div className={`mt-2 h-8 w-full rounded-xl bg-gradient-to-r ${formData.cor}`}></div>
              </div>

              {/* 🔥 NUEVO: Dos campos de imagen separados */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Imagen del Carousel */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center gap-1">
                      🖼️ Imagem do Carousel
                      <span className="text-xs text-gray-400 font-normal">(com texto)</span>
                    </span>
                  </label>
                  <div 
                    className="relative border-2 border-dashed rounded-xl p-3 text-center cursor-pointer hover:border-[#449879] transition-colors"
                    onClick={() => document.getElementById("featured-image").click()}
                  >
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Preview" className="max-h-24 mx-auto rounded-lg object-contain" />
                        <button 
                          type="button" 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setImagePreview(null); 
                            setImageFile(null);
                          }} 
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-500">Clique para selecionar</p>
                        <p className="text-[10px] text-gray-400">JPG, PNG, WEBP (max 5MB)</p>
                      </>
                    )}
                    <input 
                      id="featured-image" 
                      type="file" 
                      accept="image/*" 
                      onChange={handleImageChange} 
                      className="hidden" 
                    />
                  </div>
                </div>

                {/* 🔥 NUEVO: Imagen del Lightbox */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center gap-1">
                      🔍 Imagem Ampliada
                      <span className="text-xs text-gray-400 font-normal">(sem texto)</span>
                    </span>
                  </label>
                  <div 
                    className="relative border-2 border-dashed rounded-xl p-3 text-center cursor-pointer hover:border-[#449879] transition-colors"
                    onClick={() => document.getElementById("lightbox-image").click()}
                  >
                    {lightboxPreview ? (
                      <div className="relative">
                        <img src={lightboxPreview} alt="Preview Lightbox" className="max-h-24 mx-auto rounded-lg object-contain" />
                        <button 
                          type="button" 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            removeLightboxImage();
                          }} 
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <ImageIcon className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-500">Clique para selecionar (opcional)</p>
                        <p className="text-[10px] text-gray-400">JPG, PNG, WEBP (max 10MB)</p>
                        <p className="text-[10px] text-blue-500 mt-1">Se não enviar, usará a imagem do carousel</p>
                      </>
                    )}
                    <input 
                      id="lightbox-image" 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLightboxChange} 
                      className="hidden" 
                    />
                  </div>
                </div>
              </div>

              {/* 🔥 CHECKBOXES PARA MOSTRAR TÍTULO Y DESCRIPCIÓN */}
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={formData.show_title} 
                      onChange={(e) => setFormData({...formData, show_title: e.target.checked})} 
                      className="w-4 h-4 text-[#449879] rounded border-gray-300 focus:ring-[#449879]" 
                    />
                    <span className="text-sm text-gray-700">Mostrar título</span>
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={formData.show_description} 
                      onChange={(e) => setFormData({...formData, show_description: e.target.checked})} 
                      className="w-4 h-4 text-[#449879] rounded border-gray-300 focus:ring-[#449879]" 
                    />
                    <span className="text-sm text-gray-700">Mostrar descrição</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={formData.ativo} 
                    onChange={(e) => setFormData({...formData, ativo: e.target.checked})} 
                    className="w-4 h-4 text-[#449879] rounded" 
                  />
                  <span className="text-sm text-gray-700">Ativo</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)} 
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-xl hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={submitting} 
                  className="flex-1 bg-[#449879] text-white py-2 rounded-xl hover:bg-[#367d64] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {submitting ? "Salvando..." : editingItem ? "Atualizar" : "Criar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}